import React, { useEffect, useMemo, useState } from 'react';
import { networkManager, type ShipCargoSnapshot } from '../../systems/NetworkManager';
import { RESOURCES, getEquipmentById, DRONE_CONFIG } from '@astra/shared';

const PALETTE = {
  DARK: '#0f1b26',
  LIGHT: '#f5e8d1',
  TEAL: '#20a5a6',
  ORANGE: '#dd5639',
  DIM: '#4a5a6a',
  PANEL: '#1a2a38',
  GREEN: '#4ade80',
  RED: '#ef4444',
  BORDER: '#2a3a48',
};

interface DropItem {
  itemType: 'credits' | 'resource' | 'equipment';
  itemId: string;
  quantity: number;
}

interface DropLootModalProps {
  dropId: string;
  items: DropItem[];
  onClose: () => void;
}

const SALVAGE_LABELS: Record<string, string> = {
  miner: 'Miner Drone Salvage',
  hauler: 'Hauler Drone Salvage',
  fighter: 'Fighter Drone Salvage',
};

function formatItemLabel(itemType: string, itemId: string): string {
  if (itemType === 'credits') return 'Credits';
  if (itemType === 'resource') {
    if (itemId.startsWith(`${DRONE_CONFIG.SALVAGE_ITEM_PREFIX}_`)) {
      const role = itemId.slice(DRONE_CONFIG.SALVAGE_ITEM_PREFIX.length + 1);
      return SALVAGE_LABELS[role] ?? `${role[0]?.toUpperCase() ?? ''}${role.slice(1)} Drone Salvage`;
    }
    const r = (RESOURCES as Record<string, { name?: string }>)[itemId];
    return r?.name ?? itemId;
  }
  const eq = getEquipmentById(itemId);
  if (eq) return (eq as { name?: string }).name ?? itemId;
  return itemId.replace(/_/g, ' ');
}

function itemVolume(itemType: string, itemId: string): number {
  if (itemType === 'credits') return 0;
  if (itemType === 'resource') return 1;
  const eq = getEquipmentById(itemId);
  return (eq as { volume?: number } | undefined)?.volume ?? 1;
}

export function DropLootModal({ dropId, items, onClose }: DropLootModalProps) {
  const [cargo, setCargo] = useState<ShipCargoSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  // Fetch cargo snapshot on open
  useEffect(() => {
    const prev = networkManager.onShipCargoSnapshot;
    networkManager.onShipCargoSnapshot = (snap) => setCargo(snap);
    networkManager.sendGetShipCargoSnapshot();
    return () => { networkManager.onShipCargoSnapshot = prev; };
  }, [dropId]);

  // Listen for transfer results
  useEffect(() => {
    const onResult = (data: any) => {
      if (data.dropId !== dropId) return;
      setPending(null);
      if (!data.success) {
        setError(data.error ?? 'Transfer failed');
        setInfo(null);
        return;
      }
      setError(null);
      if (data.partial) setInfo(`Partial: only ${data.taken} fit in cargo.`);
      else setInfo(null);
      // refresh cargo
      networkManager.sendGetShipCargoSnapshot();
    };
    const prev = networkManager.onDropTransferResult;
    networkManager.onDropTransferResult = onResult;
    return () => { networkManager.onDropTransferResult = prev; };
  }, [dropId]);

  // ESC closes
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const empty = items.length === 0;

  // Cargo overflow computation
  const freeSpace = cargo ? Math.max(0, cargo.capacity - cargo.used) : Infinity;

  // Total volume needed to take everything (for TAKE ALL enable check)
  const totalVolumeNeeded = useMemo(() => {
    return items.reduce((sum, it) => {
      if (it.itemType === 'credits') return sum;
      return sum + itemVolume(it.itemType, it.itemId) * it.quantity;
    }, 0);
  }, [items]);

  const takeAllFitsExactly = cargo ? totalVolumeNeeded <= freeSpace : true;

  const handleTake = (it: DropItem, qty: number) => {
    if (qty <= 0) return;
    setPending(`${it.itemType}:${it.itemId}`);
    networkManager.sendTransferFromDrop(dropId, it.itemType, it.itemId, qty);
  };

  const handleTakeAll = () => {
    setPending('all');
    networkManager.sendTransferAllFromDrop(dropId);
  };

  // Close automatically if drop becomes empty
  useEffect(() => {
    if (empty) {
      const t = setTimeout(onClose, 600);
      return () => clearTimeout(t);
    }
  }, [empty, onClose]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, pointerEvents: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 760, maxWidth: '90vw', minHeight: '50vh', maxHeight: '80vh',
          background: PALETTE.DARK, color: PALETTE.LIGHT,
          border: `1px solid ${PALETTE.BORDER}`, borderRadius: 6,
          fontFamily: "'JetBrains Mono', monospace", display: 'flex', flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: '12px 16px', borderBottom: `1px solid ${PALETTE.BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontWeight: 600, letterSpacing: '0.08em' }}>LOOT CONTAINER</span>
          <button onClick={onClose} style={{
            background: 'transparent', color: PALETTE.LIGHT,
            border: `1px solid ${PALETTE.DIM}`, padding: '4px 12px',
            cursor: 'pointer', borderRadius: 4,
          }}>CLOSE [ESC]</button>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* LEFT: ship cargo */}
          <div style={{ flex: 1, padding: 16, borderRight: `1px solid ${PALETTE.BORDER}`, overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: PALETTE.DIM, fontSize: 11 }}>SHIP CARGO</span>
              {cargo && (
                <span style={{ color: freeSpace > 0 ? PALETTE.TEAL : PALETTE.RED, fontSize: 11 }}>
                  {cargo.used} / {cargo.capacity}
                </span>
              )}
            </div>

            {!cargo && <div style={{ color: PALETTE.DIM, fontSize: 11 }}>Loading…</div>}

            {cargo && cargo.items.length === 0 && (
              <div style={{ color: PALETTE.DIM, fontSize: 11, fontStyle: 'italic' }}>Empty.</div>
            )}

            {cargo && cargo.items.map((it) => (
              <div key={`${it.itemType}:${it.itemId}`} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: 6, marginBottom: 4,
                background: PALETTE.PANEL, border: `1px solid ${PALETTE.BORDER}`, borderRadius: 4,
              }}>
                <div style={{ fontSize: 11 }}>
                  <div style={{ color: PALETTE.LIGHT }}>{formatItemLabel(it.itemType, it.itemId)}</div>
                  <div style={{ color: PALETTE.DIM, fontSize: 10 }}>{it.quantity}× · vol {it.volume * it.quantity}</div>
                </div>
              </div>
            ))}
          </div>

          {/* RIGHT: drop contents */}
          <div style={{ flex: 1, padding: 16, overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: PALETTE.DIM, fontSize: 11 }}>DROP CONTENTS</span>
              <button
                onClick={handleTakeAll}
                disabled={empty || pending === 'all' || !takeAllFitsExactly}
                title={!takeAllFitsExactly ? 'Not enough cargo space for everything' : undefined}
                style={{
                  background: (empty || !takeAllFitsExactly) ? PALETTE.DIM : PALETTE.ORANGE,
                  color: PALETTE.DARK, border: 'none', borderRadius: 4,
                  padding: '6px 12px', fontSize: 11, fontWeight: 600,
                  cursor: (empty || !takeAllFitsExactly) ? 'not-allowed' : 'pointer',
                }}
              >TAKE ALL</button>
            </div>

            {empty && <div style={{ color: PALETTE.DIM, fontSize: 11 }}>Empty.</div>}

            {items.map((it) => {
              const key = `${it.itemType}:${it.itemId}`;
              const vol = itemVolume(it.itemType, it.itemId);
              const maxByCargo = it.itemType === 'credits' ? it.quantity : Math.floor(freeSpace / Math.max(1, vol));
              const overflow = it.itemType !== 'credits' && cargo && maxByCargo < it.quantity;
              return (
                <div key={key} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: 8,
                  background: PALETTE.PANEL, border: `1px solid ${PALETTE.BORDER}`,
                  borderRadius: 4, marginBottom: 4,
                }}>
                  <div style={{ flex: 1, fontSize: 11 }}>
                    <div style={{ color: PALETTE.LIGHT }}>{formatItemLabel(it.itemType, it.itemId)}</div>
                    <div style={{ color: PALETTE.DIM, fontSize: 10 }}>
                      {it.itemType === 'credits'
                        ? `${it.quantity.toLocaleString()} CR`
                        : `${it.quantity}× · vol ${vol * it.quantity}${overflow ? ` (fits ${maxByCargo})` : ''}`}
                    </div>
                  </div>
                  <button
                    onClick={() => handleTake(it, 1)}
                    disabled={pending === key || (it.itemType !== 'credits' && maxByCargo < 1)}
                    style={{
                      background: PALETTE.TEAL, color: PALETTE.DARK,
                      border: 'none', borderRadius: 4, padding: '4px 10px',
                      fontSize: 10, fontWeight: 600,
                      cursor: pending === key ? 'wait' : 'pointer',
                      opacity: (it.itemType !== 'credits' && maxByCargo < 1) ? 0.4 : 1,
                    }}
                  >+1</button>
                  <button
                    onClick={() => handleTake(it, it.quantity)}
                    disabled={pending === key || (it.itemType !== 'credits' && maxByCargo < 1)}
                    style={{
                      background: PALETTE.ORANGE, color: PALETTE.DARK,
                      border: 'none', borderRadius: 4, padding: '4px 10px',
                      fontSize: 10, fontWeight: 600,
                      cursor: pending === key ? 'wait' : 'pointer',
                      opacity: (it.itemType !== 'credits' && maxByCargo < 1) ? 0.4 : 1,
                    }}
                  >ALL</button>
                </div>
              );
            })}

            {error && <div style={{ color: PALETTE.RED, fontSize: 11, marginTop: 8 }}>⚠ {error}</div>}
            {info && <div style={{ color: PALETTE.ORANGE, fontSize: 11, marginTop: 8 }}>{info}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DropLootModal;
